import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Mail, Lock, ArrowRight, Loader2, WifiOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

function getLoginErrorMessage(error) {
  if (!error.response) {
    return {
      message: "We can't reach our servers right now. Please check your internet connection and try again in a moment.",
      type: 'network',
    };
  }
  const status = error.response.status;
  const detail = error.response.data?.detail;

  if (status === 401 || status === 403) {
    return {
      message: detail || "The email or password you entered doesn't match our records. Please double-check and try again.",
      type: 'credentials',
    };
  }
  if (status === 404) {
    return {
      message: "We couldn't find an account with that email. Would you like to create one?",
      type: 'not_found',
    };
  }
  if (status === 429) {
    return {
      message: 'Too many login attempts. Please wait a minute before trying again.',
      type: 'rate_limit',
    };
  }
  if (status >= 500) {
    return {
      message: "Something went wrong on our end. Our team has been notified — please try again shortly.",
      type: 'server',
    };
  }
  return {
    message: detail || 'Something unexpected happened. Please try again.',
    type: 'unknown',
  };
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, getGoogleLoginUrl } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);
    
    try {
      const userData = await login(formData.email, formData.password);
      toast.success('Welcome back!');
      // Route based on account state to avoid an extra redirect hop via ProtectedRoute
      if (userData?.role === 'client' && userData?.email_verified === false) {
        navigate('/verify-email-sent');
      } else if (userData?.role === 'client' && userData?.onboarding_completed === false) {
        navigate('/onboarding');
      } else if (userData?.role === 'client' && !userData?.package_type) {
        navigate('/dashboard/billing?from=onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const err = getLoginErrorMessage(error);
      setLoginError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoginError(null);
    try {
      const url = await getGoogleLoginUrl();
      window.location.href = url;
    } catch (error) {
      if (!error.response) {
        setLoginError({
          message: "We can't reach our servers right now. Please check your internet connection and try again in a moment.",
          type: 'network',
        });
      } else {
        toast.error('Google sign-in is temporarily unavailable. Please try email login or come back shortly.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 flex items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl">GDS</span>
          </Link>
          <h1 className="font-heading text-2xl font-bold mb-2">Welcome back</h1>
          <p className="text-slate-600 dark:text-slate-400">Sign in to your dashboard</p>
        </div>

        <Card className="border-0 shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50">
          <CardContent className="p-8">
            {loginError && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex items-start gap-3 p-4 rounded-lg mb-6 ${
                  loginError.type === 'network'
                    ? 'bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-800'
                    : loginError.type === 'not_found'
                    ? 'bg-blue-50 border border-blue-200 dark:bg-blue-950/30 dark:border-blue-800'
                    : 'bg-red-50 border border-red-200 dark:bg-red-950/30 dark:border-red-800'
                }`}
              >
                {loginError.type === 'network' ? (
                  <WifiOff className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className={`w-5 h-5 shrink-0 mt-0.5 ${
                    loginError.type === 'not_found' ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'
                  }`} />
                )}
                <div className="text-sm">
                  <p className={
                    loginError.type === 'network'
                      ? 'text-amber-800 dark:text-amber-200'
                      : loginError.type === 'not_found'
                      ? 'text-blue-800 dark:text-blue-200'
                      : 'text-red-800 dark:text-red-200'
                  }>
                    {loginError.message}
                  </p>
                  {loginError.type === 'not_found' && (
                    <Link to="/register" className="text-indigo-600 hover:text-indigo-500 font-medium mt-1 inline-block">
                      Create an account &rarr;
                    </Link>
                  )}
                  {loginError.type === 'network' && (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="text-amber-700 dark:text-amber-300 font-medium mt-1 underline hover:no-underline"
                    >
                      Try again
                    </button>
                  )}
                </div>
              </motion.div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    className="pl-10 gds-input"
                    value={formData.email}
                    onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setLoginError(null); }}
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-500">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 gds-input"
                    value={formData.password}
                    onChange={(e) => { setFormData({ ...formData, password: e.target.value }); setLoginError(null); }}
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full gds-btn-primary"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200 dark:border-slate-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white dark:bg-slate-900 text-slate-500">or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full gds-btn-secondary"
              onClick={handleGoogleLogin}
              data-testid="login-google-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </Button>

            <p className="text-center text-sm text-slate-600 dark:text-slate-400 mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-indigo-600 hover:text-indigo-500 font-medium">
                Sign up free
              </Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
